import FilterGradientIcon from 'ui-components/FilterGradientIcon';
import SearchGradientIcon from 'ui-components/SearchGradientIcon';
import React, { useState, useEffect } from "react";
import { useTheme } from "@emotion/react";
import { Autocomplete, Box, Grid, IconButton, Pagination, Typography, useMediaQuery, Drawer, Divider } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import BreadcrumbHeader from "layout/BreadcrumbsHeader";
import ThemeBGWrapper from "ui-components/ThemeBGWrapper";
import CustomInputField from "ui-components/CustomInputField";
import NoData from "ui-components/NoData";
import PerfectScrollbar from "react-perfect-scrollbar";
import { containerRef } from "utils";
import { FetchAllAnalysisResults, FetchAnalysisReports, InviteFromAnalysis } from "api/bulk-analysis";
import AnalysisCard from "./components/AnalysisCard";
import InviteDealflowModal from "./components/InviteDealflowModal";
import AllAnalysisResultsSkeleton from "../skeletons/AllAnalysisResultsSkeleton";
import { ArrowBack, Search, FilterList, Close, FilterAltOutlined, FilterAltRounded } from "@mui/icons-material";
import NoAnalysisIcon from "ui-components/icons/NoAnalysisIcon";
import { useProcessingSnackbar } from "context/ProcessingSnackbarContext";
import { registerBatchCompletionCallback, unregisterBatchCompletionCallback } from "components/ProcessingSnackbar";
import { Primary } from 'ui-components/Buttons';

const STATUS_OPTIONS = [
  { label: "All Matches", value: null },
  { label: "Strong Match (80-100)", value: "strong" },
  { label: "Good Match (70-79)", value: "good" },
  { label: "Possible Match (50-69)", value: "possible" },
  { label: "Weak Match (40-49)", value: "weak" },
  { label: "No Match (0-39)", value: "no_match" },
];

const STORAGE_KEY = 'bulk_analysis_filters';

const getInitialFilters = () => {
  try {
    if (typeof window === 'undefined') return {};
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : {};
  } catch (_) {
    return {};
  }
};

const saveFilters = (filters) => {
  try {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
    }
  } catch (_) { }
};


const AllAnalysisResultsPage = () => {
  const theme = useTheme();
  const { batches, clearCompletedBatches } = useProcessingSnackbar();
  const navigate = useNavigate();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const investorDetails = useSelector((state) => state?.investorDetails?.data);
  const c = theme.palette.customColors;

  const openAnalysis = () => {
    clearCompletedBatches();
    navigate("/bulk-analysis/start")
  };

  const [perPage] = useState(12);
  const [search, setSearch] = useState("");

  // Initialize state from URL query params
  const [page, setPage] = useState(() => {
    if (typeof window === 'undefined') return 1;
    const params = new URLSearchParams(window.location.search);
    return parseInt(params.get('page')) || 1;
  });
  const [searchQuery, setSearchQuery] = useState(() => {
    if (typeof window === 'undefined') return "";
    const params = new URLSearchParams(window.location.search);
    return params.get('search') || "";
  });

  const initialFilters = getInitialFilters();
  const [statusFilter, setStatusFilter] = useState(() => {
    if (typeof window === 'undefined') return STATUS_OPTIONS[0];
    const params = new URLSearchParams(window.location.search);
    const statusValue = params.get('status');
    if (statusValue) {
      return STATUS_OPTIONS.find(opt => opt.value === (statusValue === 'null' ? null : statusValue)) || STATUS_OPTIONS[0];
    }
    if (initialFilters.statusFilter) {
      return STATUS_OPTIONS.find(opt => opt.value === initialFilters.statusFilter) || STATUS_OPTIONS[0];
    }
    return STATUS_OPTIONS[0];
  });
  const [selectedReport, setSelectedReport] = useState(() => {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    const batchId = params.get('batchId');
    if (batchId) {
      return { batch_id: batchId };
    }
    return initialFilters.selectedReport || null;
  });
  const [inviteModal, setInviteModal] = useState({ open: false, result: null });
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [tempStatusFilter, setTempStatusFilter] = useState(null);
  const [tempSelectedReport, setTempSelectedReport] = useState(null);
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));

  // Sync state to URL query params
  useEffect(() => {
    const params = new URLSearchParams();
    if (page > 1) params.set('page', page);
    if (searchQuery) params.set('search', searchQuery);
    if (statusFilter?.value !== null) params.set('status', statusFilter?.value || 'null');
    if (selectedReport?.batch_id) params.set('batchId', selectedReport.batch_id);

    const newSearch = params.toString();
    const currentSearch = window.location.search.substring(1);
    if (newSearch !== currentSearch) {
      navigate(`?${newSearch}`, { replace: true });
    }
  }, [page, searchQuery, statusFilter, selectedReport, navigate]);

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      setSearchQuery(search);
      setPage(1);
    }
  };

  const isProcessing = Array.from(batches.values()).some(b => b.batchType === 'bulk_analysis' && b.uploadStep === 'processing');

  const { data: resultsData, refetch: refetchResults } = FetchAllAnalysisResults(
    investorDetails?._id,
    {
      page,
      limit: perPage,
      search: searchQuery,
      status_filter: statusFilter?.value || "",
      batch_id: selectedReport?.batch_id || "",
      isProcessing,
    }
  );

  const { data: reports, refetch: refetchReports } = FetchAnalysisReports(investorDetails?._id);
  const inviteMutation = InviteFromAnalysis();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isFromStart = params.get('from') === 'start';
    const batchIdFromUrl = params.get('batchId');

    if (reports && reports.length > 0) {
      // If batchId is in URL, find and select that report
      if (batchIdFromUrl && !selectedReport?.label) {
        const matchingReport = reports.find(r => r.batch_id === batchIdFromUrl);
        if (matchingReport) {
          setSelectedReport({ label: matchingReport.report_name, batch_id: matchingReport.batch_id });
        }
      }
      // If coming from start page, select latest report
      else if (isFromStart) {
        const latestReport = reports[0];
        setSelectedReport({ label: latestReport.report_name, batch_id: latestReport.batch_id });
        setStatusFilter(STATUS_OPTIONS[0]);
        window.history.replaceState(null, '', '/bulk-analysis');
      }
    }
  }, [reports]);

  useEffect(() => {
    saveFilters({
      statusFilter: statusFilter?.value,
      selectedReport,
    });
  }, [statusFilter, selectedReport]);

  useEffect(() => {
    const handleBulkAnalysisComplete = () => {
      setPage(1);
      refetchResults();
      refetchReports();
    };

    registerBatchCompletionCallback('bulk_analysis', handleBulkAnalysisComplete);

    return () => {
      unregisterBatchCompletionCallback('bulk_analysis');
    };
  }, [refetchResults, refetchReports]);

  const reportOptions = reports
    ? [{ label: "All Reports", batch_id: null }, ...reports.map((r) => ({ label: r.report_name || 'Untitled Report', batch_id: r.batch_id }))]
    : [{ label: "All Reports", batch_id: null }];

  const handleInviteSend = async (result) => {
    try {
      await inviteMutation.mutateAsync({
        batch_id: result.batch_id,
        file_id: result.file_id,
        email: result.email,
        startup_name: result.startup_name,
        message: result.message,
      });
      setInviteModal({ open: false, result: null });
      refetchResults();
    } catch (err) {
      console.error("Failed to send invite:", err);
    }
  };

  const hasResults = resultsData?.results?.length > 0;
  const hasAnyResults = (resultsData?.total ?? 0) > 0;

  const isFiltered = !!searchQuery || (statusFilter?.value !== null && statusFilter?.value !== "all") || !!selectedReport?.batch_id;

  // Auto-redirect to upload page when no analyses exist
  useEffect(() => {
    if (resultsData && !hasAnyResults && !isFiltered) {
      navigate("/bulk-analysis/start");
    }
  }, [hasAnyResults, isFiltered, resultsData, navigate]);

  return (
    <ThemeBGWrapper>

      <BreadcrumbHeader
        title="Bulk Deck Analysis"
        breadcrumbItems={[
          { title: "Tools", path: "" },
          { title: "All Results", path: "/bulk-analysis" },
        ]}
        sx={{ paddingBottom: "0.5rem !important" }}
      />

      <Box sx={{ padding: isSmallScreen ? "0rem 1rem 1rem 1rem" : "0 2rem 2rem 2rem" }}>
        {/* Toolbar */}
        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, sm: 1 }, mb: { xs: 1, sm: 2 }, flexWrap: "nowrap" }}>
          {/* Search Field */}
          <Box sx={{ flex: "1", minWidth: 0 }}>
            <CustomInputField
              autoComplete="off"
              fullWidth
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyPress={handleSearchKeyDown}
              placeholder="Search by startup / deck name"
              startAdornment={
                <SearchGradientIcon />
              }
              sx={{
                marginBottom: 0,
                "& > .MuiFilledInput-root": {
                  borderColor: `${c.b50} !important`,
                  borderRadius: "8px",
                  height: "44px",
                },
              }}
            />
          </Box>

          {/* Desktop Filters (hidden on mobile/tablet) */}
          {!isMobileOrTablet && (
            <>
              {/* Status filter */}
              <Box sx={{ flex: "0.8", minWidth: 0 }}>
                <Autocomplete
                  autoHighlight
                  options={STATUS_OPTIONS}
                  value={statusFilter || STATUS_OPTIONS[0]}
                  onChange={(_, val) => { setStatusFilter(val); setPage(1); }}
                  getOptionLabel={(o) => o.label}
                  isOptionEqualToValue={(o, v) => o.value === v.value}
                  sx={{ minWidth: 200 }}
                  renderInput={(params) => (
                    <CustomInputField variant="outlined" placeholder="All Status" {...params} sx={{
                      marginBottom: 0, "& .MuiOutlinedInput-root .MuiOutlinedInput-input ": {
                        padding: "0.4rem"
                      }
                    }} className="filter-autocomplete" />
                  )}
                />
              </Box>

              {/* Report filter */}
              <Box sx={{ flex: "0.8", minWidth: 0 }}>
                <Autocomplete
                  autoHighlight
                  options={reportOptions}
                  value={selectedReport || reportOptions[0]}
                  onChange={(_, val) => { setSelectedReport(val?.batch_id ? val : null); setPage(1); }}
                  getOptionLabel={(o) => o?.label || 'All Reports'}
                  isOptionEqualToValue={(o, v) => o?.batch_id === v?.batch_id}
                  sx={{ minWidth: 200 }}
                  renderInput={(params) => (
                    <CustomInputField variant="outlined" placeholder="All Reports" {...params} sx={{
                      marginBottom: 0, "& .MuiOutlinedInput-root .MuiOutlinedInput-input ": {
                        padding: "0.4rem"
                      }
                    }} className="filter-autocomplete" />
                  )}
                />
              </Box>
            </>
          )}

          {/* Mobile/Tablet Filter Button */}
          {isMobileOrTablet && (
            <IconButton
              onClick={() => {
                setTempStatusFilter(statusFilter);
                setTempSelectedReport(selectedReport);
                setFilterDrawerOpen(true);
              }}
              sx={{
                border: `1px solid ${c.b50}`,
                borderRadius: "8px",
                width: 44,
                height: 44,
                backgroundColor: c.whiteBG,
                "&:hover": { backgroundColor: c.inputField },
                flexShrink: 0,
              }}
            >
              <FilterGradientIcon active={isFiltered} />
            </IconButton>
          )}

          {/* Start Over Button */}
          <Primary
            onClick={() => !isProcessing && openAnalysis()}
            disabled={isProcessing}
            sx={{ textTransform: "none", padding: "0.6rem 1.25rem", borderRadius: "8px", height: 44, "&:disabled": { color: "white" }, flexShrink: 0 }}
          >
            {isProcessing ? "Processing..." : "Start Over"}
          </Primary>
        </Box>

        {/* Filter Drawer for Mobile/Tablet */}
        <Drawer
          anchor="bottom"
          open={filterDrawerOpen}
          onClose={() => {
            setTempStatusFilter(statusFilter);
            setTempSelectedReport(selectedReport);
            setFilterDrawerOpen(false);
          }}
          PaperProps={{
            sx: {
              borderTopLeftRadius: "16px",
              borderTopRightRadius: "16px",
              maxHeight: "80vh",
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-end',
              maxWidth: 500, // maxWidth + horizontal padding
              mx: 'auto'
            },
          }}
        >
          <Box sx={{ p: 2, minWidth: 0, width: 'stretch', maxWidth: 500 + 2 * 16, mx: 'auto' }}>
            {/* Header */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: c.g300 }}>
                Filters
              </Typography>
              <IconButton size="small" onClick={() => setFilterDrawerOpen(false)}>
                <Close sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Status Filter */}
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: c.g300, mb: 1 }}>
                Status
              </Typography>
              <Autocomplete
                autoHighlight
                options={STATUS_OPTIONS}
                value={tempStatusFilter}
                onChange={(_, val) => setTempStatusFilter(val)}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(o, v) => o.value === v.value}
                fullWidth
                renderInput={(params) => (
                  <CustomInputField variant="outlined" placeholder="All Status" {...params} sx={{
                    marginBottom: 0, "& .MuiOutlinedInput-root .MuiOutlinedInput-input ": {
                      padding: "0.4rem"
                    }
                  }} className="filter-autocomplete" />
                )}
              />
            </Box>

            {/* Report Filter */}
            <Box sx={{ mb: 3 }}>
              <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: c.g300, mb: 1 }}>
                Report
              </Typography>
              <Autocomplete
                autoHighlight
                options={reportOptions}
                value={tempSelectedReport || reportOptions[0]}
                onChange={(_, val) => setTempSelectedReport(val?.batch_id ? val : null)}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(o, v) => o.batch_id === v?.batch_id}
                fullWidth
                renderInput={(params) => (
                  <CustomInputField variant="outlined" placeholder="All Reports" {...params} sx={{
                    marginBottom: 0, "& .MuiOutlinedInput-root .MuiOutlinedInput-input ": {
                      padding: "0.4rem"
                    }
                  }} className="filter-autocomplete" />
                )}
              />
            </Box>

            {/* Apply Button */}
            <Primary
              fullWidth
              onClick={() => {
                setStatusFilter(tempStatusFilter);
                setSelectedReport(tempSelectedReport);
                setPage(1);
                setFilterDrawerOpen(false);
              }}
              sx={{ height: 44, fontSize: "1rem" }}
            >
              Apply Filters
            </Primary>
          </Box>
        </Drawer>
        <Box containerRef={containerRef} sx={{ height: { xs: "calc(100dvh - 8.5rem)", sm: "calc(-9.5rem + 100dvh)", md: "calc(-11rem + 100dvh)" }, overflow:  "auto", paddingRight: isSmallScreen ? "0rem" : "1rem", scrollbarWidth: isSmallScreen || !resultsData ? "none" : "thin" }}>

          {
            !resultsData ? <AllAnalysisResultsSkeleton /> :
              <>

                {/* Result count */}
                {/* {hasResults && (
                  <Typography sx={{ fontSize: "0.875rem", color: c.g200, mb: 2 }}>
                    Showing {resultsData.results.length} of {resultsData.total} result{resultsData.total !== 1 ? "s" : ""}
                  </Typography>
                )} */}

                {/* Results grid or empty state */}
                {hasResults ? (
                  <>
                    <Grid container spacing={2.5} rowSpacing={{ xs: 2.5, sm: 2.5, md: 3 }}>
                      {resultsData.results.map((result) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={result.file_id}>
                          <AnalysisCard
                            result={result}
                            onInvite={(r) => setInviteModal({ open: true, result: r })}
                            showBatchInfo
                          />
                        </Grid>
                      ))}
                    </Grid>
                    {resultsData?.total_pages > 1 && (
                      <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                        <Pagination
                          count={resultsData.total_pages}
                          page={page}
                          onChange={(_, p) => setPage(p)}
                          color="primary"
                          shape="rounded"
                        />
                      </Box>
                    )}
                  </>
                ) : (
                  <NoData
                    placeHolderImage={NoAnalysisIcon}
                    title={isFiltered ? "No matching results" : hasAnyResults ? "No results yet" : "No analysis yet"}
                    // subTitle={
                    //   isFiltered
                    //     ? "Try adjusting your search, status filter, or report selection."
                    //     : "Upload pitch decks to get AI-powered scoring and see results here."
                    // }
                    subTitle=''
                    Actions={() => (
                      !isFiltered && (
                        <Primary
                          onClick={() => !isProcessing && openAnalysis()}
                          disabled={isProcessing}
                          sx={{ textTransform: "none", padding: "0.6rem 1.25rem", borderRadius: "8px" }}
                        >
                          {isProcessing ? "Processing" : "Analyze Pitch Decks"}
                        </Primary>
                      )
                    )}
                  />
                )}
              </>
          }
        </Box>
      </Box>

      <InviteDealflowModal
        open={inviteModal.open}
        onClose={() => setInviteModal({ open: false, result: null })}
        result={inviteModal.result}
        onSend={handleInviteSend}
        isLoading={inviteMutation.isLoading}
      />

    </ThemeBGWrapper>
  );
};

export default AllAnalysisResultsPage;
